import { useEffect, useState } from 'react'

export interface DiscussLinkData {
  exists: boolean
  topicUrl?: string
  replyCount?: number
  lastPostAt?: string
  recentAvatars?: { username: string; avatarUrl: string }[]
}

export interface DiscussCTAProps {
  entityType: 'story' | 'cve' | 'briefing_item'
  entityId: string
}

/**
 * `<DiscussCTA>` — surfaces an entity's Community Discuss state with three states:
 *  - cold: no Discourse topic exists yet (`exists: false`)
 *  - warm-0: topic exists, no replies (`exists: true, replyCount: 0`)
 *  - warm-N: topic exists with replies (renders count + recent avatars + "last X ago")
 *
 * Always links to `/discuss/:entityType/:entityId` (server resolves to topic or seeds one).
 * Falls back to cold state on fetch error so the CTA never disappears.
 */
export function DiscussCTA({ entityType, entityId }: DiscussCTAProps) {
  const [data, setData] = useState<DiscussLinkData | null>(null)

  useEffect(() => {
    let cancelled = false
    fetch(`/api/community/links/${entityType}/${entityId}`)
      .then((r) => r.json())
      .then((json: DiscussLinkData) => {
        if (!cancelled) setData(json)
      })
      .catch(() => {
        if (!cancelled) setData({ exists: false })
      })
    return () => {
      cancelled = true
    }
  }, [entityType, entityId])

  const href = `/discuss/${entityType}/${entityId}`

  if (!data) {
    return (
      <a className="cta-discuss-loading" href={href}>
        💬 Discuss in Community
      </a>
    )
  }

  if (!data.exists) {
    return (
      <a className="cta-discuss" href={href}>
        💬 Discuss in Community
      </a>
    )
  }

  const replyCount = data.replyCount ?? 0
  if (replyCount === 0) {
    return (
      <a className="cta-discuss" href={href}>
        💬 Discuss in Community · topic active, no replies yet
      </a>
    )
  }

  return (
    <a className="cta-discuss" href={href}>
      💬 {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
      {data.recentAvatars?.length ? (
        <span className="avatars">
          {data.recentAvatars.map((a) => (
            // R28: alt/title carry a real community member's username, which
            // maskTextSelector does not cover (it masks text nodes, not attributes).
            <img
              key={a.username}
              data-ph-no-capture
              src={a.avatarUrl}
              alt={a.username}
              title={`@${a.username}`}
            />
          ))}
        </span>
      ) : null}
      <span className="last-post"> · last {timeAgo(data.lastPostAt)}</span>
    </a>
  )
}

function timeAgo(iso?: string): string {
  if (!iso) return ''
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}
